import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';


import { MultiSelect } from '@mantine/core';
import { useThrottledCallback } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
// ─────────────────────────── imports actualizados ────────────────────────────
import {
  Customized,
  ReferenceArea,
  ReferenceLine,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  ResponsiveContainer,
} from 'recharts';

import { VerticalRangeSlider } from './VerticalRangeSlider';
import { ReactComponent as SaveIcon } from '../../react-icons/floppyDisk.svg';
import { ReactComponent as MinusIcon } from '../../react-icons/minusIcon.svg';
import { ReactComponent as PlusIcon } from '../../react-icons/plusIcon.svg';
import { ReactComponent as TrashIcon } from '../../react-icons/trashIcon.svg';
import { type TrackPoint, type HighlightSegment, type Tack } from '../../types';

import './LineChartPanel.css';
import './themeInversion.css';
/* eslint-disable react/prop-types */
/* -------------------- constantes -------------------- */
const EXCLUDED_METRICS = new Set(['lat', 'lon', 'globalIndex']);
const RESIZE_LEFT_TEXT = 'resize-left';
const TARGET_POINTS = 750;
const MAX_SELECTED_METRICS = 40;
const DRAG_THROTTLE_MS = 40;

const DECIMALS_BY_METRIC: Record<string, number> = {
  twd: 1,
  vmg: 1,
  bsppc: 1,
  vmgpc: 1,
  bsp: 1,
  sog: 1,
  heel: 1,
  twa: 1,
  tws: 1,
  rudder: 1,
  twa_abs: 1,
  rudder_abs: 1,
  rot: 1,
  hdg: 1,
  trim: 1,
};

const PRIMARY_BACKGROUND = 'var(--surface)';
const CHART_ACCENT_COLOR = 'var(--chart-3)';
const CHART_TEXT_COLOR = 'var(--contrast)';
const CHART_TICK_COLOR = 'var(--textMutedStrong, var(--textMuted))';
const CHART_GRID_COLOR = 'var(--borderSubtle)';
const TEXT_CONTRAST_COLOR = 'var(--textContrastHigh, var(--contrast))';
const MULTI_SELECT_STYLES = {
  input: {
    color: TEXT_CONTRAST_COLOR,
  },
  searchInput: {
    color: TEXT_CONTRAST_COLOR,
  },
  label: {
    color: TEXT_CONTRAST_COLOR,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
};
const LOCALE_ES = 'es-ES';
const TACK_COLOR = 'var(--chart-4)';
const GYBE_COLOR = 'var(--chart-2)';

const LABEL_POS_BOTTOM_LEFT = 'insideBottomLeft' as const;
const LABEL_POS_TOP_LEFT = 'insideTopLeft' as const;
const STYLE_POINTER_NONE = { pointerEvents: 'none' } as const;
function makeAreaFromIndices(
  trackData: TrackPoint[],
  index1: number,
  index2: number,
  metric: string,
  color: string,
  fillOpacity = 0.35,
): ReferenceAreaEnhanced | null {
  if (!Array.isArray(trackData) || trackData.length === 0) {
    return null;
  }
  if (index1 < 0 || index2 < 0) {
    return null;
  }
  if (index1 >= trackData.length || index2 >= trackData.length) {
    return null;
  }

  const startIndex = Math.min(index1, index2);
  const endIndex = Math.max(index1, index2);
  const startUtc = trackData[startIndex]?.utc;
  const endUtc = trackData[endIndex]?.utc;
  if (!startUtc || !endUtc) {
    return null;
  }
  const tsStart = new Date(startUtc).getTime();
  const tsEnd = new Date(endUtc).getTime();
  if (!Number.isFinite(tsStart) || !Number.isFinite(tsEnd)) {
    return null;
  }
  const [start, end] = tsStart <= tsEnd ? [tsStart, tsEnd] : [tsEnd, tsStart];

  const segment = trackData.slice(startIndex, endIndex + 1);
  if (segment.length === 0) {
    return null;
  }

  const vals = segment
    .map((d) => (d as unknown as AnyRecord)[metric])
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
  const avg = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  const last = vals.at(-1);
  const first = vals[0];
  const diff = last !== undefined && first !== undefined ? last - first : 0;

  return { x1: start, x2: end, fill: color, fillOpacity, avg, diff, startIndex, endIndex };
}

function computeReferenceAreas(
  trackData: TrackPoint[],
  selectedMetrics: string[],
  highlightSegments: HighlightSegment[],
  pendingStartIndex: number | null,
  hoverEndIndex: number | null,
  pendingHighlightColor: string,
  mapDraft: DraftHighlight | null,
): ReferenceAreaEnhanced[] {
  const areas: ReferenceAreaEnhanced[] = [];
  if (!Array.isArray(trackData) || trackData.length === 0) {
    return areas;
  }
  const metric = selectedMetrics[0] ?? 'bsp';

  // Persisted highlights
  for (const [segmentIndex, seg] of highlightSegments.entries()) {
    const end = seg.endIndex;
    if (typeof seg.startIndex !== 'number' || typeof end !== 'number') {
      continue;
    }
    const area = makeAreaFromIndices(trackData, seg.startIndex, end, metric, seg.color);
    if (area) {
      areas.push({ ...area, segmentIndex });
    }
  }

  // Pending selection
  if (pendingStartIndex !== null && hoverEndIndex !== null) {
    const area = makeAreaFromIndices(
      trackData,
      pendingStartIndex,
      hoverEndIndex,
      metric,
      pendingHighlightColor,
    );
    if (area) {
      areas.push(area);
    }
  }

  if (mapDraft && (mapDraft.source === 'map' || mapDraft.source === 'chartPreview')) {
    const area = makeAreaFromIndices(
      trackData,
      mapDraft.startIndex,
      mapDraft.endIndex ?? mapDraft.startIndex,
      metric,
      mapDraft.color,
    );
    if (area) {
      areas.push(area);
    }
  }

  return areas;
}

type AnyRecord = Record<string, unknown>;

type RechartsMouseEvent = {
  activeLabel?: number | string;
  activePayload?: Array<{ payload?: { timestamp?: number } }>;
  isTooltipActive?: boolean;
};

interface ChartPoint {
  timestamp: number;
  time: string;
  [key: string]: number | string | undefined;
}

const formatTime = (utc: string): string => {
  const date = new Date(utc);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString(LOCALE_ES, {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
};

/* -------------------- tipado utils -------------------- */
// eslint-disable-next-line @typescript-eslint/ban-types
type ScaleFunction = ((value: number) => number) & {
  invert: (px: number) => number;
  domain: () => [number, number];
};

type DraftHighlight = HighlightSegment & { source: 'map' | 'chart' | 'chartPreview' };

/* -------------------- utilidades -------------------- */

/**
 * Resample uniforme de datos a 'count' puntos.
 */

const getGradientStops = (metric: string) =>
  metric === 'heel'
    ? [
        { offset: '0%', color: 'var(--success)' },
        { offset: '50%', color: 'var(--textMutedStrong, var(--textMuted))' },
        { offset: '100%', color: 'var(--destructive)' },
      ]
    : [
        { offset: '0%', color: 'var(--chart-1)' },
        { offset: '50%', color: 'var(--chart-2)' },
        { offset: '100%', color: 'var(--chart-3)' },
      ];

interface ChartWrapperProperties {
  basisPercent: number;
  children: React.ReactNode;
}

/**
 * Envoltorio para garantizar que cada gráfica ocupa
 * un % fijo del contenedor vertical y permite scroll cuando hay más de cuatro métricas.
 */
const ChartWrapper: React.FC<ChartWrapperProperties> = ({ basisPercent, children }) => (
  <div
    style={{
      height: `${basisPercent}%`,
      position: 'relative',
      minHeight: '200px',
      minWidth: '300px',
      width: '100%',
    }}
  >
    {children}
  </div>
);

/* ======================================================================== */
/*                             MetricChart                                  */
/* ======================================================================== */
interface ReferenceAreaEnhanced {
  x1: number;
  x2: number;
  fill: string;
  fillOpacity?: number;
  avg: number;
  diff: number;
  startIndex: number;
  endIndex: number;
  segmentIndex?: number;
}

interface MetricChartProperties {
  metric: string;
  decimals: number;
  stats: { min: number; max: number; avg: number };
  baseChartData: Array<
    { timestamp: number; time: string } & { [key: string]: number | string | undefined }
  >;
  xDomain: [number, number];
  referenceAreas: ReferenceAreaEnhanced[];
  canSaveArea: boolean;
  chartHeightPercent: number;
  /* ←— NUEVO: eventos de maniobra */
  events: Array<{ timestamp: number; type: 'tack' | 'gybe' }>;
}

interface MetricChartHandlers {
  onSaveArea: (index: number) => void;
  onDeleteArea: (index: number) => void;
  onChartClick: (timestamp: number) => void;
  onDragArea: (index: number, x1: number, x2: number) => void;
  onChartHover?: (timestamp: number | null) => void;
  onPreviewArea?: (startIndex: number, endIndex: number, color: string) => void;
  onPreviewEnd?: () => void;
}

const MetricChartComponent: React.FC<MetricChartProperties & MetricChartHandlers> = ({
  metric,
  decimals,
  stats,
  baseChartData,
  xDomain,
  chartHeightPercent: _chartHeightPercent,
  referenceAreas,
  canSaveArea,
  events,
  onSaveArea,
  onDeleteArea,
  onChartClick,
  onDragArea,
  onChartHover,
  onPreviewArea,
  onPreviewEnd,
}) => {
  const dragReference = useRef<{
    areaIdx: number;
    segmentIndex: number;
    mode: 'move' | 'resize-left' | 'resize-right';
    startClientX: number;
    startX1: number;
    startX2: number;
    xScale: ScaleFunction;
  } | null>(null);

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [lastDraggedIndex, setLastDraggedIndex] = useState<number | null>(null);

  const TITLE_HEIGHT = 12;
  const ignoreNextClickReference = useRef(false);
  const gradientId = `gradient-${metric}`;

  const titleElement = (
    <div
      style={{
        position: 'absolute',
        top: `${TITLE_HEIGHT / 2}px`,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: PRIMARY_BACKGROUND,
        padding: '2px 8px',
        fontWeight: 'bold',
        fontSize: 12,
        zIndex: 2,
        borderRadius: 4,
        border: `1px solid ${CHART_GRID_COLOR}`,
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
        color: CHART_TEXT_COLOR,
      }}
    >
      {metric.replaceAll('_', ' ').toUpperCase()}
    </div>
  );

  // Recharts pasa un estado con activeLabel:string|number; aceptamos unknown y coaccionamos.
  const handleClick = useCallback(
    (event_: unknown) => {
      const eventState = event_ as RechartsMouseEvent | undefined;
      const tsFromPayload = eventState?.activePayload?.[0]?.payload?.timestamp;
      const raw = eventState?.activeLabel;
      const label =
        typeof raw === 'number' ? raw : Number.isFinite(Number(raw)) ? Number(raw) : undefined;
      const timestamp = typeof tsFromPayload === 'number' ? tsFromPayload : label;
      if (timestamp == null) {
        return;
      }
      onChartClick(timestamp);
    },
    [onChartClick],
  );

  const handleMouseMove = useCallback(
    (event_: unknown) => {
      if (!onChartHover) {
        return;
      }
      const eventState = event_ as RechartsMouseEvent | undefined;
      if (!eventState?.isTooltipActive) {
        onChartHover(null);
        return;
      }
      const tsFromPayload = eventState.activePayload?.[0]?.payload?.timestamp;
      const raw = eventState.activeLabel;
      const label =
        typeof raw === 'number' ? raw : Number.isFinite(Number(raw)) ? Number(raw) : undefined;
      const timestamp = typeof tsFromPayload === 'number' ? tsFromPayload : label;
      if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
        onChartHover(null);
        return;
      }
      onChartHover(timestamp);
    },
    [onChartHover],
  );

  const handleMouseLeave = useCallback(() => {
    onChartHover?.(null);
    onPreviewEnd?.();
  }, [onChartHover, onPreviewEnd]);

  const renderAreaButtons = (properties: {
    xAxisMap?: Record<string, { scale?: unknown }> | Array<{ scale?: unknown }>;
    offset: { left: number; right: number; top: number; bottom: number };
    height: number;
    width: number;
  }) => {
    const { xAxisMap, offset, height, width } = properties;

    const rawXAxisMap: Record<string, { scale?: unknown }> | Array<{ scale?: unknown }> =
      xAxisMap ?? ({} as Record<string, { scale?: unknown }>);
    const firstAxis = Array.isArray(rawXAxisMap)
      ? rawXAxisMap[0]
      : (Object.values(rawXAxisMap)[0] as { scale?: unknown } | undefined);

    if (!firstAxis || typeof firstAxis.scale !== 'function') {
      return null;
    }

    type ScaleLike = ((value: number) => number) & {
      invert?: (px: number) => number;
      domain?: () => [number, number];
    };
    const rawScale = firstAxis.scale as ScaleLike;

    const xScale: ScaleFunction = ((v: number) => {
      const result = rawScale(v);
      return typeof result === 'number' ? result : Number(result);
    }) as ScaleFunction;

    const invertFunction = (typeof rawScale.invert === 'function' ? rawScale.invert : undefined) as
      | ((px: number) => number)
      | undefined;
    xScale.invert = invertFunction
      ? (px: number) => Number(invertFunction(px))
      : (_px: number) => 0;

    const domainFunction = (typeof rawScale.domain === 'function' ? rawScale.domain : undefined) as
      | (() => [number | string, number | string])
      | undefined;
    xScale.domain = domainFunction
      ? () => {
          const d = domainFunction();
          const a = Array.isArray(d) && d.length >= 2 ? [Number(d[0]), Number(d[1])] : [0, 0];
          return [Number.isFinite(a[0]) ? a[0] : 0, Number.isFinite(a[1]) ? a[1] : 0];
        }
      : () => [0, 0];

    const ICON_SIZE = 16;
    const SPACING = 5;
    const MARGIN_BOTTOM = 4;
    const PADDING = 4;
    const EDGE_W = 6;

    const chartHeight = height - offset.top - offset.bottom;
    const y = offset.top + chartHeight - ICON_SIZE - MARGIN_BOTTOM;
    const labelY = offset.top + 12;

    const clipId = `clip-${metric}-buttons`;

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const onPointerMove = (event_: PointerEvent) => {
      if (!dragReference.current) {
        return;
      }
      const {
        areaIdx,
        segmentIndex,
        mode,
        startClientX,
        startX1,
        startX2,
        xScale: scale,
      } = dragReference.current;
      const dxPx = event_.clientX - startClientX;
      const deltaData = scale.invert(scale(startX1) + dxPx) - startX1;
      let newX1 = startX1;
      let newX2 = startX2;
      if (mode === 'move') {
        newX1 += deltaData;
        newX2 += deltaData;
      } else if (mode === RESIZE_LEFT_TEXT) {
        newX1 += deltaData;
      } else {
        newX2 += deltaData;
      }
      const domainPair: [number, number] = scale.domain();
      const d0 = domainPair[0];
      const d1 = domainPair[1];
      newX1 = Math.max(d0, Math.min(newX1, d1));
      newX2 = Math.max(d0, Math.min(newX2, d1));
      onDragArea(segmentIndex, newX1, newX2);
    };
    const endDrag = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('mouseup', endDrag);
      document.body.classList.remove('no-select');
      dragReference.current && setLastDraggedIndex(dragReference.current.areaIdx);
      setDraggingIndex(null);
    };
    const startDrag = (
      index: number,
      segmentIndex: number,
      mode: 'move' | 'resize-left' | 'resize-right',
      event_: React.PointerEvent | React.MouseEvent,
      area: { x1: number; x2: number },
    ) => {
      ignoreNextClickReference.current = true;
      if (mode !== 'move') {
        event_.stopPropagation();
      }

      const clientX = event_.clientX;

      dragReference.current = {
        areaIdx: index,
        segmentIndex,
        mode,
        startClientX: clientX,
        startX1: area.x1,
        startX2: area.x2,
        xScale,
      };
      document.body.classList.add('no-select');
      setDraggingIndex(index);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('mouseup', endDrag, { once: true });
    };

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const handlePointerLeave = (index: number) => {
      if (lastDraggedIndex === index) {
        setLastDraggedIndex(null);
      }
    };

    const clipWidth = width - offset.left - offset.right;
    const clipHeight = height - offset.top - offset.bottom;

    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect x={offset.left} y={offset.top} width={clipWidth} height={clipHeight} />
          </clipPath>
        </defs>

        {referenceAreas.map((area, index) => {
          const x1Px = xScale(area.x1);
          const x2Px = xScale(area.x2);
          const centerPx = (x1Px + x2Px) / 2;
          const areaWidth = Math.abs(x2Px - x1Px);
          const midRectWidth = Math.max(0, areaWidth - 2 * EDGE_W);
          const color = area.fill;

          const isPermanent = area.segmentIndex !== undefined;
          const isDragging = draggingIndex === index;
          const isLastDragged = lastDraggedIndex === index;
          const handleAreaPointerEnter = () => {
            if (!isPermanent || !onPreviewArea) {
              return;
            }
            onPreviewArea(area.startIndex, area.endIndex, color);
          };
          const handleAreaPointerLeave = () => {
            if (isPermanent && onPreviewEnd) {
              onPreviewEnd();
            }
            handlePointerLeave(index);
          };

          return (
            <g
              key={index}
              className={`area-group ${
                isDragging ? 'dragging' : isLastDragged ? 'last-dragged' : ''
              }`}
              onPointerEnter={handleAreaPointerEnter}
              onPointerLeave={handleAreaPointerLeave}
            >
              {isPermanent && (
                <>
                  <rect
                    x={Math.min(x1Px, x2Px)}
                    y={offset.top}
                    width={EDGE_W}
                    height={chartHeight}
                    fill="transparent"
                    pointerEvents="all"
                    cursor="ew-resize"
                    onPointerDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, RESIZE_LEFT_TEXT, event_, area);
                    }}
                    onMouseDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, RESIZE_LEFT_TEXT, event_, area);
                    }}
                  />
                  <rect
                    x={Math.min(x1Px, x2Px) + EDGE_W}
                    y={offset.top}
                    width={midRectWidth}
                    height={chartHeight}
                    fill="transparent"
                    pointerEvents="all"
                    cursor="move"
                    onPointerDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, 'move', event_, area);
                    }}
                    onMouseDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, 'move', event_, area);
                    }}
                  />
                  <rect
                    x={Math.max(x1Px, x2Px) - EDGE_W}
                    y={offset.top}
                    width={EDGE_W}
                    height={chartHeight}
                    fill="transparent"
                    pointerEvents="all"
                    cursor="ew-resize"
                    onPointerDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, 'resize-right', event_, area);
                    }}
                    onMouseDown={(event_) => {
                      if (area.segmentIndex === undefined) {
                        return;
                      }
                      startDrag(index, area.segmentIndex, 'resize-right', event_, area);
                    }}
                  />
                </>
              )}

              <g clipPath={`url(#${clipId})`}>
                <text
                  x={centerPx}
                  y={labelY}
                  fill={CHART_TEXT_COLOR}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight="bold"
                >{`AVG ${area.avg.toFixed(decimals)}`}</text>
                <text
                  x={centerPx}
                  y={labelY + 14}
                  fill={CHART_TEXT_COLOR}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight="bold"
                >{`Δ ${area.diff.toFixed(decimals)}`}</text>

                {isPermanent && (
                  <>
                    {canSaveArea && (
                      <g
                        className="icon-wrapper"
                        transform={`translate(${centerPx - ICON_SIZE - SPACING - PADDING},${
                          y - PADDING
                        })`}
                        style={{ cursor: 'pointer', color }}
                        onClick={(event_) => {
                          event_.stopPropagation();
                          if (area.segmentIndex === undefined) {
                            return;
                          }
                          onSaveArea(area.segmentIndex);
                        }}
                      >
                        <rect
                          className="bg"
                          x={0}
                          y={0}
                          width={ICON_SIZE + PADDING * 2}
                          height={ICON_SIZE + PADDING * 2}
                          rx={4}
                          ry={4}
                          fill={PRIMARY_BACKGROUND}
                          stroke="currentColor"
                        />
                        <SaveIcon
                          className="icon"
                          width={ICON_SIZE}
                          height={ICON_SIZE}
                          x={PADDING}
                          y={PADDING}
                          style={{ color: '#15394f' }}
                        />
                      </g>
                    )}
                    <g
                      className="icon-wrapper"
                      transform={`translate(${centerPx + SPACING - PADDING},${y - PADDING})`}
                      style={{ cursor: 'pointer', color }}
                      onClick={(event_) => {
                        event_.stopPropagation();
                        if (area.segmentIndex === undefined) {
                          return;
                        }
                        onDeleteArea(area.segmentIndex);
                      }}
                    >
                      <rect
                        className="bg"
                        x={0}
                        y={0}
                        width={ICON_SIZE + PADDING * 2}
                        height={ICON_SIZE + PADDING * 2}
                        rx={4}
                        ry={4}
                        fill={PRIMARY_BACKGROUND}
                        stroke="currentColor"
                      />
                      <TrashIcon
                        className="icon"
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                        fill="var(--contrast)"
                        x={PADDING}
                        y={PADDING}
                      />
                    </g>
                  </>
                )}
              </g>
            </g>
          );
        })}
        <style>{`
          .icon-wrapper .icon { opacity: 0; transition: opacity .2s; }
          .icon-wrapper .bg   { opacity: 0; transition: opacity .2s; }
          .area-group:hover .icon-wrapper .icon { opacity: 1; }
          .icon-wrapper:hover .bg { opacity: 1; }
          .area-group.dragging .icon-wrapper .icon,
          .area-group.last-dragged .icon-wrapper .icon { opacity: 1; }
        `}</style>
      </g>
    );
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        paddingTop: `${TITLE_HEIGHT}px`,
        background: PRIMARY_BACKGROUND,
      }}
    >
      {titleElement}
      <ResponsiveContainer>
        <LineChart
          data={baseChartData}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {getGradientStops(metric).map(({ offset, color }) => (
                <stop key={offset} offset={offset} stopColor={color} />
              ))}
            </linearGradient>
          </defs>

          {events.map((event_, index) => (
            <ReferenceLine
              key={`tackArea-${index}`}
              x={event_.timestamp}
              y1={stats.min}
              y2={stats.max}
              stroke={event_.type === 'tack' ? TACK_COLOR : GYBE_COLOR}
              strokeOpacity={0.75}
              strokeWidth={2}
              strokeDasharray="4 2"
              ifOverflow="hidden"
              label={{
                position: 'insideTopRight',
                value: event_.type.toUpperCase(),
                angle: -90,
                dx: -6,
                fill: event_.type === 'tack' ? TACK_COLOR : GYBE_COLOR,
                fontSize: 11,
                fontWeight: 'bold',
              }}
            />
          ))}

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={xDomain}
            allowDataOverflow
            tickFormatter={(ts) =>
              new Date(ts as number).toLocaleTimeString(LOCALE_ES, {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            }
            tickCount={10}
            tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
            stroke={CHART_GRID_COLOR}
            padding={{ left: 5, right: 5 }}
          />

          <YAxis
            domain={[stats.min, stats.max]}
            tickCount={10}
            stroke={CHART_GRID_COLOR}
            tick={{ fill: CHART_TICK_COLOR }}
            tickFormatter={(v: number) => v.toFixed(decimals)}
          />
          <ReferenceLine
            y={stats.min}
            stroke={CHART_ACCENT_COLOR}
            strokeOpacity={0.65}
            strokeDasharray="3 3"
            label={{
              value: `MIN ${stats.min.toFixed(decimals)}`,
              position: LABEL_POS_BOTTOM_LEFT,
              fill: CHART_TEXT_COLOR,
            }}
            style={STYLE_POINTER_NONE}
          />
          <ReferenceLine
            y={stats.max}
            stroke={CHART_ACCENT_COLOR}
            strokeOpacity={0.65}
            strokeDasharray="3 3"
            label={{
              value: `MAX ${stats.max.toFixed(decimals)}`,
              position: LABEL_POS_TOP_LEFT,
              fill: CHART_TEXT_COLOR,
            }}
            style={STYLE_POINTER_NONE}
          />
          <ReferenceLine
            y={stats.avg}
            stroke={CHART_ACCENT_COLOR}
            strokeOpacity={0.65}
            strokeDasharray="3 3"
            label={{
              value: `AVG ${stats.avg.toFixed(decimals)}`,
              position: LABEL_POS_BOTTOM_LEFT,
              fill: CHART_TEXT_COLOR,
            }}
            style={STYLE_POINTER_NONE}
          />
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} strokeOpacity={0.4} />
          <Tooltip
            wrapperStyle={{ outline: 'none' }}
            cursor={{ stroke: CHART_ACCENT_COLOR, strokeDasharray: '4 4' }}
            contentStyle={{
              background: 'var(--surface)',
              border: `1px solid ${CHART_GRID_COLOR}`,
              borderRadius: 8,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              color: CHART_TEXT_COLOR,
            }}
            labelStyle={{ color: CHART_TEXT_COLOR, fontWeight: 600 }}
            itemStyle={{ color: CHART_TEXT_COLOR, fontSize: 12 }}
            formatter={(value: unknown, name: string) => {
              const label = name.replaceAll('_', ' ').toUpperCase();
              if (typeof value === 'number') {
                return [value.toFixed(decimals), label] as [string, string];
              }
              return [String(value), label] as [string, string];
            }}
            labelFormatter={(ts) =>
              new Date(ts as number).toLocaleTimeString(LOCALE_ES, {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            }
          />
          <Line
            type="monotone"
            dataKey={metric}
            stroke={`url(#${gradientId})`}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />

          {referenceAreas.map((area, index) => (
            <ReferenceArea
              key={index}
              x1={area.x1}
              x2={area.x2}
              y1={stats.min}
              y2={stats.max}
              strokeOpacity={0}
              fill={area.fill}
              fillOpacity={area.fillOpacity}
              ifOverflow="hidden"
              style={STYLE_POINTER_NONE}
            />
          ))}
          {/* Cast para TS en Recharts Customized */}
          <Customized component={renderAreaButtons as unknown as React.FC<unknown>} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const MetricChart = memo(MetricChartComponent);
MetricChart.displayName = 'MetricChart';
(MetricChart as React.MemoExoticComponent<typeof MetricChart>).displayName = 'MetricChart';

/* ======================================================================== */
/*                           Helper pure functions                          */
/* ======================================================================== */
function buildRelativeIndexByGlobal(trackData: TrackPoint[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const [index, p] of trackData.entries()) {
    if (typeof p.globalIndex === 'number') {
      m.set(p.globalIndex, index);
    }
  }
  return m;
}

function findPeakIndex(trackData: TrackPoint[], startIndex: number, endIndex: number): number {
  const segment = trackData.slice(startIndex, endIndex + 1);
  if (segment.length === 0) {
    return startIndex;
  }
  const hasRudder = segment.some((p) => typeof (p as unknown as AnyRecord).rudder_abs === 'number');
  const key = hasRudder ? 'rudder_abs' : 'rot';
  let maxIndex = startIndex;
  let maxValue = -Infinity;
  for (const [index, p] of segment.entries()) {
    const vRaw = (p as unknown as AnyRecord)[key];
    const v = typeof vRaw === 'number' ? Math.abs(vRaw) : 0;
    if (v > maxValue) {
      maxValue = v;
      maxIndex = startIndex + index;
    }
  }
  return maxIndex;
}

function classifyManeuverType(previousTwa: number, currentTwa: number): 'tack' | 'gybe' {
  const avgTwa = (Math.abs(previousTwa) + Math.abs(currentTwa)) / 2;
  return avgTwa < 90 ? 'tack' : 'gybe';
}

function makeChartPointFromTrack(p: TrackPoint, selectedMetrics: string[]): ChartPoint {
  const cp: ChartPoint = {
    timestamp: new Date(p.utc).getTime(),
    time: formatTime(p.utc),
  };
  for (const m of selectedMetrics) {
    const raw = (p as unknown as AnyRecord)[m];
    cp[m] = typeof raw === 'number' ? raw : Number.isFinite(Number(raw)) ? Number(raw) : undefined;
  }
  return cp;
}

/* ======================================================================== */
/*                             LineChartPanel                               */
/* ======================================================================== */
export interface LineChartPanelProperties {
  trackData: TrackPoint[];
  highlightSegments: HighlightSegment[];
  saveHighlightAsTraining: (segment: HighlightSegment) => void;
  canManageSelectedFile: boolean;
  removeHighlight: (index: number) => void;
  addHighlight: (startIndex: number, endIndex?: number, color?: string) => void;
  updateHighlight: (index: number, startIndex: number, endIndex: number, color: string) => void;
  generateRandomColor: () => string;
  draftHighlight: DraftHighlight | null;
  setDraftHighlight: React.Dispatch<
    React.SetStateAction<DraftHighlight | null>
  >;
  /* ←— NUEVO: lista de maniobras */
  tacks: Tack[];
}

const LineChartPanel: React.FC<LineChartPanelProperties> = ({
  trackData,
  highlightSegments,
  saveHighlightAsTraining,
  canManageSelectedFile,
  removeHighlight,
  addHighlight,
  updateHighlight,
  generateRandomColor,
  draftHighlight,
  setDraftHighlight,
  tacks,
}) => {
  /* -------------------------------------------------------------------- */
  /* 1. Mapa timestamp -> índice para búsqueda O(1)                        */
  /* -------------------------------------------------------------------- */
  const timestampIndexMap = useMemo(() => {
    const m = new Map<number, number>();
    for (const [index, p] of trackData.entries()) {
      m.set(new Date(p.utc).getTime(), index);
    }
    return m;
  }, [trackData]);

  const trackTimestamps = useMemo(() => {
    let previousValid = 0;
    return trackData.map((point) => {
      const ts = new Date(point.utc).getTime();
      if (Number.isFinite(ts)) {
        previousValid = ts;
        return ts;
      }
      return previousValid;
    });
  }, [trackData]);

  /* -------------------------------------------------------------------- */
  /* 2. Convertimos los tacks a eventos con timestamp y tipo tack/gybe    */
  /* -------------------------------------------------------------------- */
  const tackEvents = useMemo(() => {
    if (!Array.isArray(tacks) || tacks.length === 0 || trackData.length === 0) {
      return [];
    }

    const relatedByGlobal = buildRelativeIndexByGlobal(trackData);

    const events: Array<{ timestamp: number; type: 'tack' | 'gybe' }> = [];
    for (const tk of tacks) {
      const s = tk as Partial<{ start_index: number; startIndex: number }>;
      const end = tk as Partial<{ end_index: number; endIndex: number }>;
      const absStart = typeof s.start_index === 'number' ? s.start_index : s.startIndex;
      const absEnd = typeof end.end_index === 'number' ? end.end_index : end.endIndex;
      if (typeof absStart !== 'number' || typeof absEnd !== 'number') {
        continue;
      }

      const rs = relatedByGlobal.get(absStart);
      const re = relatedByGlobal.get(absEnd);
      if (rs === undefined || re === undefined) {
        continue;
      }

      const startIndex = Math.min(rs, re);
      const endIndex = Math.max(rs, re);
      const peakIndex = findPeakIndex(trackData, startIndex, endIndex);
      const current = trackData[peakIndex];
      if (!current?.utc) {
        continue;
      }

      const previousIndex = Math.max(peakIndex - 1, 0);
      const previousTwa = trackData[previousIndex]?.twa ?? 0;
      const type = classifyManeuverType(previousTwa, current.twa ?? 0);
      events.push({ timestamp: new Date(current.utc).getTime(), type });
    }

    return events;
  }, [tacks, trackData]);

  /* ------------------- métricas disponibles SIN vacíos ------------------- */
  const metricKeys = useMemo(() => {
    if (trackData.length === 0) {
      return [];
    }
    return Object.keys(trackData[0])
      .filter(
        (k) =>
          typeof k === 'string' &&
          k.trim().length > 0 && // ← descarta nombres vacíos
          k !== 'utc' &&
          !EXCLUDED_METRICS.has(k),
      )
      .filter((k) =>
        trackData.some((p) => {
          const v = (p as unknown as AnyRecord)[k];
          return v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v));
        }),
      ); // ← descarta métricas sin datos
  }, [trackData]);

  /* -------- opciones del selector construidas a partir de metricKeys ----- */
  const metricOptions = useMemo(() => {
    return metricKeys.map((key) => ({
      value: key,
      label: key.replaceAll('_', ' ').toUpperCase(),
    }));
  }, [metricKeys]);

  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(metricKeys.slice(0, 1));
  const hasUserSelectedMetricsRef = useRef(false);
  const chartHeightPercent = useMemo(() => {
    const count = selectedMetrics.length;
    return count > 0 ? Math.max(100 / count, 25) : 100;
  }, [selectedMetrics.length]);
  const [range, setRange] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    setSelectedMetrics((current) => {
      const stillAvailable = current.filter((metric) => metricKeys.includes(metric));
      if (stillAvailable.length > 0) {
        return stillAvailable;
      }
      if (current.length === 0 && hasUserSelectedMetricsRef.current) {
        return current;
      }
      return metricKeys.length > 0 ? [metricKeys[0]] : [];
    });
  }, [metricKeys]);

  useEffect(() => {
    if (trackData.length > 0) {
      setRange([0, trackData.length - 1]);
    }
  }, [trackData]);

  useEffect(() => {
    if (trackData.length === 0) {
      return;
    }

    setRange((previous) => {
      const max = trackData.length - 1;
      const start = Math.min(previous[0], max);
      const end = Math.min(previous[1], max);

      // si el rango queda invertido, volvemos al rango completo
      return start <= end ? [start, end] : [0, max];
    });
  }, [trackData.length]);

  const visibleEvents = useMemo(() => {
    if (tackEvents.length === 0 || trackData.length === 0) {
      return [];
    }

    const maxIndex = trackData.length - 1;
    const startIndex = Math.min(range[0], maxIndex);
    const endIndex = Math.min(range[1], maxIndex);

    const t0 = new Date(trackData[startIndex].utc).getTime();
    const t1 = new Date(trackData[endIndex].utc).getTime();

    return tackEvents.filter((event_) => event_.timestamp >= t0 && event_.timestamp <= t1);
  }, [tackEvents, trackData, range]);

  /* -------------------------------------------------------------------- */
  /*  chartData: muestreo con paso estable para evitar “vibración”         */
  /* -------------------------------------------------------------------- */
  /* -------------------------------------------------------------------- */
  /* 1. Índices muestreados globalmente (se calcula una sola vez)         */
  /* -------------------------------------------------------------------- */
  const sampledIndices = useMemo<number[]>(() => {
    if (trackData.length === 0) {
      return [];
    }
    const skip = Math.max(1, Math.floor(trackData.length / TARGET_POINTS));
    const idxs: number[] = [];
    for (let index = 0; index < trackData.length; index += skip) {
      idxs.push(index);
    }
    // por si acaso, garantizamos que el último punto real también entra
    if (idxs.at(-1) !== trackData.length - 1) {
      idxs.push(trackData.length - 1);
    }
    return idxs;
  }, [trackData]);

  /* -------------------------------------------------------------------- */
  /* 2. chartData: filtramos los índices muestreados que están en range   */
  /* -------------------------------------------------------------------- */
  /* -------------------------------------------------------------------- */
  /*  Datos que alimentan los <LineChart/>                                */
  /* -------------------------------------------------------------------- */
  const chartData = useMemo<ChartPoint[]>(() => {
    if (trackData.length === 0) {
      return [];
    }

    const maxIndex = trackData.length - 1;
    const r0 = Math.min(range[0], maxIndex);
    const r1 = Math.min(range[1], maxIndex);
    const selectedIndices = new Set<number>([r0, r1]);

    for (const index of sampledIndices) {
      if (index < r0 || index > r1) {
        continue;
      }
      selectedIndices.add(index);
    }

    return [...selectedIndices]
      .sort((left, right) => left - right)
      .map((index) => makeChartPointFromTrack(trackData[index], selectedMetrics));
  }, [trackData, sampledIndices, range, selectedMetrics]);

  const chartDomain = useMemo<[number, number]>(() => {
    if (trackData.length === 0) {
      return [0, 0];
    }
    const maxIndex = trackData.length - 1;
    const r0 = Math.min(Math.max(range[0], 0), maxIndex);
    const r1 = Math.min(Math.max(range[1], 0), maxIndex);
    const start = new Date(trackData[r0]?.utc ?? '').getTime();
    const end = new Date(trackData[r1]?.utc ?? '').getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return [0, 0];
    }
    return start <= end ? [start, end] : [end, start];
  }, [range, trackData]);

  /* -------------------------------------------------------------------- */
  /*  Estadísticas MIN / MAX / AVG de cada métrica                        */
  /* -------------------------------------------------------------------- */
  const statsByMetric = useMemo(() => {
    const stats: Record<string, { min: number; max: number; avg: number }> = {};
    if (trackData.length === 0 || selectedMetrics.length === 0) {
      return stats;
    }

    const maxIndex = trackData.length - 1;
    const r0 = Math.min(range[0], maxIndex);
    const r1 = Math.min(range[1], maxIndex);

    for (const metric of selectedMetrics) {
      let min = Infinity,
        max = -Infinity,
        sum = 0,
        count = 0;

      for (let index = r0; index <= r1; index++) {
        const v = (trackData[index] as unknown as AnyRecord)[metric];
        if (typeof v !== 'number' || Number.isNaN(v)) {
          continue;
        }
        min = Math.min(min, v);
        max = Math.max(max, v);
        sum += v;
        count++;
      }
      if (count) {
        stats[metric] = { min, max, avg: sum / count };
      }
    }
    return stats;
  }, [trackData, range, selectedMetrics]);

  const handleMetricsChange = useCallback(
    (values: string[]) => {
      hasUserSelectedMetricsRef.current = true;
      setSelectedMetrics(values.filter((v) => metricKeys.includes(v)));
    },
    [metricKeys],
  );

  const [hoverEndIndex, setHoverEndIndex] = useState<number | null>(null);
  const [pendingStartIndex, setPendingStartIndex] = useState<number | null>(null);
  const [pendingHighlightColor, setPendingHighlightColor] = useState<string>('#888');

  useEffect(() => {
    if (pendingStartIndex === null || hoverEndIndex === null) {
      setDraftHighlight((current) => (current && current.source === 'chart' ? null : current));
      return;
    }
    const start = Math.min(pendingStartIndex, hoverEndIndex);
    const end = Math.max(pendingStartIndex, hoverEndIndex);
    setDraftHighlight({
      id: 'draft-chart',
      startIndex: start,
      endIndex: end,
      color: pendingHighlightColor,
      source: 'chart',
    });
  }, [hoverEndIndex, pendingHighlightColor, pendingStartIndex, setDraftHighlight]);

  const findClosestTrackIndex = useCallback(
    (ts: number): number => {
      const direct = timestampIndexMap.get(ts);
      if (direct !== undefined) {
        return direct;
      }
      if (trackTimestamps.length === 0) {
        return 0;
      }
      let low = 0;
      let high = trackTimestamps.length - 1;
      let closestIndex = 0;
      let minDiff = Infinity;
      while (low <= high) {
        const mid = (low + high) >> 1;
        const midTs = trackTimestamps[mid];
        if (!Number.isFinite(midTs)) {
          if (mid === 0) {
            low = mid + 1;
          } else {
            high = mid - 1;
          }
          continue;
        }
        const diff = Math.abs(midTs - ts);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = mid;
        }
        if (midTs === ts) {
          return mid;
        }
        if (midTs < ts) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      return closestIndex;
    },
    [timestampIndexMap, trackTimestamps],
  );

  /* ===================================================================== */
  /*  handleAreaDrag completo                                              */
  /* ===================================================================== */
  const handleAreaDrag = useThrottledCallback((segIndex: number, ts1: number, ts2: number) => {
    if (trackData.length === 0) {
      return;
    }
    const index1 = findClosestTrackIndex(ts1);
    const index2 = findClosestTrackIndex(ts2);
    const [start, end] = index1 <= index2 ? [index1, index2] : [index2, index1];
    const color = highlightSegments[segIndex]?.color ?? generateRandomColor();
    updateHighlight(segIndex, start, end, color);
  }, DRAG_THROTTLE_MS);

  const handleAreaPreview = useCallback(
    (startIndex: number, endIndex: number, color: string) => {
      if (pendingStartIndex !== null) {
        return;
      }
      setDraftHighlight((current) => {
        if (
          current &&
          current.source === 'chartPreview' &&
          current.startIndex === startIndex &&
          current.endIndex === endIndex &&
          current.color === color
        ) {
          return current;
        }
        return { id: 'draft-chart-preview', startIndex, endIndex, color, source: 'chartPreview' };
      });
    },
    [pendingStartIndex, setDraftHighlight],
  );

  const handleAreaPreviewEnd = useCallback(() => {
    setDraftHighlight((current) => (current?.source === 'chartPreview' ? null : current));
  }, [setDraftHighlight]);

  useEffect(() => {
    return () => {
      setDraftHighlight((current) => (current?.source === 'chartPreview' ? null : current));
    };
  }, [setDraftHighlight]);

  useEffect(() => {
    if (!draftHighlight || draftHighlight.source !== 'chartPreview') {
      return;
    }
    const expectedEnd = draftHighlight.endIndex ?? draftHighlight.startIndex;
    const draftStart = Math.min(draftHighlight.startIndex, expectedEnd);
    const draftEnd = Math.max(draftHighlight.startIndex, expectedEnd);
    const stillExists = highlightSegments.some((segment) => {
      if (typeof segment.startIndex !== 'number' || typeof segment.endIndex !== 'number') {
        return false;
      }
      const segStart = Math.min(segment.startIndex, segment.endIndex);
      const segEnd = Math.max(segment.startIndex, segment.endIndex);
      return segStart === draftStart && segEnd === draftEnd;
    });
    if (!stillExists) {
      setDraftHighlight((current) => (current?.source === 'chartPreview' ? null : current));
    }
  }, [draftHighlight, highlightSegments, setDraftHighlight]);

  const handleChartClick = useCallback(
    (timestamp: number) => {
      const index = timestampIndexMap.get(timestamp);
      if (index === undefined) {
        return;
      }
      if (pendingStartIndex === null) {
        const color = generateRandomColor();
        setPendingHighlightColor(color);
        setPendingStartIndex(index);
        setHoverEndIndex(index);
      } else {
        addHighlight(pendingStartIndex, index, pendingHighlightColor);
        setPendingStartIndex(null);
        setHoverEndIndex(null);
      }
    },
    [
      pendingStartIndex,
      timestampIndexMap,
      addHighlight,
      generateRandomColor,
      pendingHighlightColor,
    ],
  );

  const handleChartHover = useCallback(
    (timestamp: number | null) => {
      if (pendingStartIndex === null) {
        return;
      }
      if (timestamp === null) {
        setHoverEndIndex((current) =>
          current === pendingStartIndex ? current : pendingStartIndex,
        );
        return;
      }
      const index = findClosestTrackIndex(timestamp);
      setHoverEndIndex((current) => (current === index ? current : index));
    },
    [pendingStartIndex, findClosestTrackIndex],
  );

  /* -------------------------------------------------------------------- */
  /*  Áreas de referencia (segmentos destacados)                          */
  /* -------------------------------------------------------------------- */
  const referenceAreas = useMemo<ReferenceAreaEnhanced[]>(() => {
    return computeReferenceAreas(
      trackData,
      selectedMetrics,
      highlightSegments,
      pendingStartIndex,
      hoverEndIndex,
      pendingHighlightColor,
      draftHighlight,
    );
  }, [
    highlightSegments,
    pendingStartIndex,
    hoverEndIndex,
    trackData,
    pendingHighlightColor,
    draftHighlight,
    selectedMetrics,
  ]);

  const handleZoomIn = useCallback(() => {
    if (trackData.length === 0) {
      return;
    }
    const [start, end] = range;
    const width = end - start;
    const newWidth = Math.max(1, Math.floor(width / 2));
    const center = Math.floor((start + end) / 2);
    let newStart = center - Math.floor(newWidth / 2);
    let newEnd = newStart + newWidth;
    if (newStart < 0) {
      newStart = 0;
      newEnd = newWidth;
    }
    if (newEnd > trackData.length - 1) {
      newEnd = trackData.length - 1;
      newStart = newEnd - newWidth;
    }
    setRange([newStart, newEnd]);
  }, [range, trackData.length]);

  const handleZoomOut = useCallback(() => {
    if (trackData.length === 0) {
      return;
    }
    const [start, end] = range;
    const width = end - start;
    const newWidth = Math.min(trackData.length - 1, Math.ceil(width * 2));
    const center = Math.floor((start + end) / 2);
    let newStart = center - Math.floor(newWidth / 2);
    let newEnd = newStart + newWidth;
    if (newStart < 0) {
      newStart = 0;
      newEnd = Math.min(newWidth, trackData.length - 1);
    }
    if (newEnd > trackData.length - 1) {
      newEnd = trackData.length - 1;
      newStart = Math.max(0, newEnd - newWidth);
    }
    setRange([newStart, newEnd]);
  }, [range, trackData.length]);
  const { t } = useTranslation('common');
  if (!trackData || trackData.length === 0) {
    return (
      <div className="line-chart-panel theme-inverted">
        <MultiSelect
          className="metric-selector"
          placeholder={t('lineChart.searchMetricsPlaceholder', 'Search metrics')}
          data={metricOptions}
          value={selectedMetrics}
          onChange={handleMetricsChange}
          maxValues={MAX_SELECTED_METRICS}
          disabled={metricOptions.length === 0}
          clearable
          searchable
          styles={MULTI_SELECT_STYLES}
        />
        <div className="no-metrics-selected">{t('lineChart.noDataSelected', 'No data selected. Select a file.')}</div>;
      </div>
    );
  }
  return (
    <div className="line-chart-panel theme-inverted">
      <MultiSelect
        className="metric-selector"
        placeholder={t('lineChart.searchMetricsPlaceholder', 'Search metrics')}
        data={metricOptions}
        value={selectedMetrics}
        onChange={handleMetricsChange}
        maxValues={MAX_SELECTED_METRICS}
        disabled={metricOptions.length === 0}
        clearable
        searchable
        styles={MULTI_SELECT_STYLES}
      />

      {selectedMetrics.length === 0 ? (
        <div className="no-metrics-selected">{t('lineChart.noMetricsSelected', 'No metrics selected')}</div>
      ) : (
        <>
          <VerticalRangeSlider
            orientation="horizontal"
            min={0}
            max={trackData.length - 1}
            value={range}
            onChange={setRange}
            style={{ marginLeft: 40, width: 'calc(100% - 40px)', height: 4 }}
          />
          <div className="charts-container">
            <div className="zoom-container">
              <button
                className="zoom-button zoom-in"
                onClick={handleZoomIn}
                aria-label={t('lineChart.zoomInAria', 'Zoom in')}
              >
                <PlusIcon width={16} height={16} />
              </button>
              <button
                className="zoom-button zoom-out"
                onClick={handleZoomOut}
                aria-label={t('lineChart.zoomOutAria', 'Zoom out')}
              >
                <MinusIcon width={16} height={16} />
              </button>
            </div>
            {selectedMetrics.map((metric) => {
              const decimals = DECIMALS_BY_METRIC[metric] ?? 2;
              const stats = statsByMetric[metric] ?? { min: 0, max: 0, avg: 0 };
              return (
                <ChartWrapper key={metric} basisPercent={chartHeightPercent}>
                  <MetricChart
                    metric={metric}
                    decimals={decimals}
                    stats={stats}
                    chartHeightPercent={chartHeightPercent}
                    baseChartData={chartData}
                    xDomain={chartDomain}
                    referenceAreas={referenceAreas}
                    events={visibleEvents}
                    canSaveArea={canManageSelectedFile}
                    onChartClick={handleChartClick}
                    onChartHover={handleChartHover}
                    onSaveArea={(segmentIndex) => {
                      const segment = highlightSegments[segmentIndex];
                      if (segment) {
                        saveHighlightAsTraining(segment);
                      }
                    }}
                    onDeleteArea={removeHighlight}
                    onDragArea={handleAreaDrag}
                    onPreviewArea={handleAreaPreview}
                    onPreviewEnd={handleAreaPreviewEnd}
                  />
                </ChartWrapper>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

LineChartPanel.displayName = 'LineChartPanel';
export { LineChartPanel };
